import React from 'react';
import { Pressable, StyleSheet } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { Loading } from '@/components/ui/loading/loading';
import { STRINGS } from '@/constants/strings';
import { useGuardianPlayersQuery } from '@/features/guardian/queries';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import {
  type PendingPlayer,
  useOnboardingPlayersStore,
} from '@/features/onboarding/stores/onboarding-players-store';
import {
  bustAvatarCache,
  toMatchedPlayer,
  toPendingMatchedPlayer,
} from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { useSessionStore } from '@/stores/session-store';
import { colors, guardianColors } from '@/theme/colors';

import { OnboardingAddPlayerSheet } from './onboarding-add-player-sheet';
import { PlayerCard } from './player-card';

const strings = STRINGS.onboarding.step2;

// Figma colours that aren't in the shared palette yet.
const BLUE_GUARDIAN = '#346ae1';
const ICON_CIRCLE_BG = '#e8f1ff';
const MATCH_GRADIENT = ['#d8ffe5', '#e1ffe6'];

/**
 * Onboarding step 2 — "Your players". Shows the golfers a coach has
 * already linked to the guardian's email, with a confirmation banner and a way
 * to add another. The wizard's footer drives "Continue" / "Back".
 */
export function OnboardingStepMatchPlayers({
  avatarCacheBust = 0,
}: {
  /** Bumped by the screen's pull-to-refresh so matched avatars re-fetch even when their
   *  URL is unchanged. 0 (the default) leaves the URLs untouched. */
  avatarCacheBust?: number;
} = {}) {
  const { scale } = useScaling();
  const styles = useStyles();

  const uid = useSessionStore(s => s.user?.uid);
  const email = useSessionStore(s => s.user?.email) ?? '';
  const { data, isLoading } = useGuardianPlayersQuery(uid);
  const pendingPlayers = useOnboardingPlayersStore(s => s.pendingPlayers);

  // Coach-linked players come from the API; queued players are ones the guardian
  // just added that haven't been saved yet (created on "Continue").
  const matched = React.useMemo(
    () => (data ?? []).map(player => bustAvatarCache(toMatchedPlayer(player), avatarCacheBust)),
    [data, avatarCacheBust],
  );
  const pending = React.useMemo(
    () =>
      pendingPlayers.map(player =>
        bustAvatarCache(toPendingMatchedPlayer(player), avatarCacheBust),
      ),
    [pendingPlayers, avatarCacheBust],
  );
  const players = React.useMemo(() => [...matched, ...pending], [matched, pending]);

  // A single sheet drives "add" (editing/viewing = null), "edit" (editing = the
  // queued player) and "view" (viewing = the saved server player). Editing prefills
  // from the queued payload; viewing prefills read-only from the API player.
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PendingPlayer | null>(null);
  const [viewing, setViewing] = React.useState<GuardianPlayer | null>(null);

  const openAdd = () => {
    setEditing(null);
    setViewing(null);
    setSheetOpen(true);
  };

  const openEdit = (id: string) => {
    const target = pendingPlayers.find(p => p.id === id);
    if (!target) return;
    setViewing(null);
    setEditing(target);
    setSheetOpen(true);
  };

  // Open a saved (server) player read-only. The card view model drops the API
  // fields, so look the raw player up from the query data by id.
  const openView = (id: string) => {
    const target = (data ?? []).find(p => p.id === id);
    if (!target) return;
    setEditing(null);
    setViewing(target);
    setSheetOpen(true);
  };

  const [before, after] =
    matched.length > 0
      ? strings.subtitle.split('{email}')
      : strings.noPlayerSubtitle.split('{email}');

  return (
    <Stack>
      {/* Heading */}
      <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
        {strings.title}
      </Text>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
        {before}
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.email}>
          {email}
        </Text>
        {after}
      </Text>

      {/* Spinner + "Loading…" while the coach-linked players are being fetched. */}
      {isLoading ? (
        <Stack marginTop={scale(24)} align="center" justify="center">
          <Loading />
        </Stack>
      ) : null}

      {/* Matched banner — counts only the coach-linked players the API found. */}
      {matched.length > 0 ? (
        <LinearGradient
          colors={MATCH_GRADIENT}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.banner}>
          <Stack
            direction="row"
            align="center"
            gap={scale(4)}
            paddingX={scale(12)}
            paddingY={scale(8)}>
            <SvgIcon name="IcUserCheck" size={scale(24)} color={colors.Success} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.bannerText}>
              {strings.matched(matched.length)}
            </Text>
          </Stack>
        </LinearGradient>
      ) : null}

      {/* Matched players */}
      {players.length > 0 ? (
        <Stack gap={scale(12)} marginTop={scale(16)}>
          {players.map(player => (
            <PlayerCard
              key={player.id}
              player={player}
              onEdit={player.isPending ? () => openEdit(player.id) : undefined}
              onPress={player.isPending ? () => openEdit(player.id) : () => openView(player.id)}
            />
          ))}
        </Stack>
      ) : null}

      {/* Add another */}
      <Stack marginTop={scale(16)}>
        <AddPlayerButton onPress={openAdd} />
      </Stack>

      <OnboardingAddPlayerSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        player={editing}
        viewPlayer={viewing}
      />
    </Stack>
  );
}

type AddPlayerButtonProps = { onPress: () => void };

/** Dashed-border CTA to link an additional player. */
function AddPlayerButton({ onPress }: AddPlayerButtonProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Pressable onPress={onPress} style={styles.addButton}>
      <Stack style={styles.addIconCircle} align="center" justify="center">
        <SvgIcon name="IcPlusBold" size={scale(34)} color={guardianColors['Main/Main']} />
      </Stack>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.addLabel}>
        {strings.addPlayer}
      </Text>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: colors['10'],
  },
  subtitle: {
    marginTop: scale(8),
    color: colors['10'],
  },
  email: {
    color: colors.Blue,
  },
  banner: {
    marginTop: scale(12),
    borderRadius: scale(12),
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.Success,
    overflow: 'hidden',
  },
  bannerText: {
    flex: 1,
    color: colors.Success,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: BLUE_GUARDIAN,
    padding: scale(12),
  },
  addIconCircle: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(30),
    backgroundColor: ICON_CIRCLE_BG,
  },
  addLabel: {
    color: colors.Blue,
  },
}));

export default OnboardingStepMatchPlayers;
